import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Copy, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";

const JITSI_DOMAIN = "meet.jit.si";

function loadJitsi() {
  return new Promise((resolve, reject) => {
    if (window.JitsiMeetExternalAPI) return resolve();
    const s = document.createElement("script");
    s.src = `https://${JITSI_DOMAIN}/external_api.js`;
    s.async = true;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

export default function SecureMeet() {
  const { user } = useApp();
  const search = new URLSearchParams(useLocation().search);
  const room = search.get("room") || `My Kind-${Math.random().toString(36).slice(2, 10)}`;
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const [ready, setReady] = useState(false);
  const inviteLink = `${window.location.origin}/meet?room=${encodeURIComponent(room)}`;

  useEffect(() => {
    let mounted = true;
    loadJitsi().then(() => {
      if (!mounted || !containerRef.current) return;
      apiRef.current = new window.JitsiMeetExternalAPI(JITSI_DOMAIN, {
        roomName: room,
        parentNode: containerRef.current,
        width: "100%",
        height: "100%",
        userInfo: { displayName: user?.name || "My Kind Guest" },
        configOverwrite: { prejoinPageEnabled: true, disableDeepLinking: true, defaultLanguage: "en" },
        interfaceConfigOverwrite: { DEFAULT_BACKGROUND: "#05050A", DISABLE_JOIN_LEAVE_NOTIFICATIONS: false, SHOW_JITSI_WATERMARK: false },
      });
      setReady(true);
    }).catch(() => toast.error("Couldn't load the video engine. Check your connection and retry."));
    return () => { mounted = false; try { apiRef.current?.dispose(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(inviteLink); toast.success("Invite link copied — share it with anyone."); }
    catch { toast(inviteLink); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-24 pb-10">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><ShieldCheck size={14} /> My Kind Secure Meet</span>
        <span className="text-xs text-sec">Room · <b className="text-[var(--rud-text)]">{room}</b></span>
        <button data-testid="copy-invite-link" onClick={copy} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5 ml-auto"><Copy size={14} /> Copy invite link</button>
      </div>
      <div className="relative w-full rounded-2xl overflow-hidden border border-rud bg-black" style={{ height: "72vh", minHeight: 420 }}>
        {!ready && <div className="absolute inset-0 grid place-items-center text-sec"><span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Architecting your secure room…</span></div>}
        <div ref={containerRef} data-testid="jitsi-frame" className="w-full h-full" />
      </div>
      <p className="text-[11px] text-sec mt-3">Secure room powered by Jitsi. Anyone with the invite link can join — no account required.</p>
    </div>
  );
}
